import type { UseFormReturn } from 'react-hook-form'
import { fieldLabels } from '@/features/consignment-form/labels'
import { PHONE_LENGTH } from '@/features/consignment-form/rules'
import type { Consignment } from '@/features/consignment-form/schema'
import { CompanyFieldset } from '@/features/consignment-form/sections/CompanyFieldset'
import { t } from '@/lang'
import { Checkbox } from '@/ui/Checkbox'
import { FormField } from '@/ui/FormField'
import { Input } from '@/ui/Input'
import { Section } from '@/ui/Section'

type Props = {
  form: UseFormReturn<Consignment>
  /** Which party this is: names the fields, their labels, and prefixes every control id in the section. */
  party: 'shipper' | 'consignee'
}

/** Shipper and consignee are the same block with different wording. */
export function PartySection({ form, party }: Props) {
  const { register, formState, watch } = form
  const errors = formState.errors[party]

  return (
    <Section title={t(`party.${party}.title`)}>
      <div className="grid gap-x-5 gap-y-2 md:grid-cols-3 md:gap-x-7.5">
        <FormField
          htmlFor={`${party}-name`}
          label={fieldLabels[`${party}.name`]}
          error={errors?.name?.message}
          required
        >
          <Input
            id={`${party}-name`}
            placeholder={t('party.name-placeholder')}
            autoComplete="given-name"
            {...register(`${party}.name`)}
          />
        </FormField>
        <FormField
          htmlFor={`${party}-surname`}
          label={fieldLabels[`${party}.surname`]}
          error={errors?.surname?.message}
          required
        >
          <Input
            id={`${party}-surname`}
            placeholder={t('party.surname-placeholder')}
            autoComplete="family-name"
            {...register(`${party}.surname`)}
          />
        </FormField>
        <FormField
          htmlFor={`${party}-phone`}
          label={fieldLabels[`${party}.contactNumber`]}
          error={errors?.contactNumber?.message}
          required
        >
          <Input
            id={`${party}-phone`}
            type="tel"
            aria-label={t(`party.${party}.phone-label`)}
            placeholder={t('party.phone-placeholder')}
            autoComplete={`section-${party} tel`}
            maxLength={PHONE_LENGTH}
            {...register(`${party}.contactNumber`)}
          />
        </FormField>
        <FormField
          htmlFor={`${party}-email`}
          label={fieldLabels[`${party}.email`]}
          error={errors?.email?.message}
          required
        >
          <Input
            id={`${party}-email`}
            type="email"
            placeholder={t(`party.${party}.email-placeholder`)}
            autoComplete="email"
            {...register(`${party}.email`)}
          />
        </FormField>
      </div>
      <Checkbox
        id={`${party}-is-company`}
        label={fieldLabels[`${party}.legalEntity`]}
        className="mt-2 md:mt-5"
        {...register(`${party}.legalEntity`)}
      />
      {watch(`${party}.legalEntity`) ? <CompanyFieldset form={form} party={party} /> : null}
    </Section>
  )
}
